import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { 
  Save, 
  ArrowLeft, 
  Upload, 
  X, 
  Info, 
  Layers, 
  Zap, 
  Clock, 
  Trash2, 
  Plus, 
  Loader2,
  FileCode,
  DollarSign,
  TrendingUp,
  AlertTriangle,
  Package
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/Card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { Badge } from '@/components/ui/Badge';
import api from '@/lib/api';
import { 
  VisibilidadeProduto, 
  TipoManufatura, 
  Filamento, 
  Maquina, 
  CustoCalculado 
} from '@/types';

export const NovoProduto = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('geral');
  const [isUploading, setIsUploading] = useState(false);
  const [isCalculando, setIsCalculando] = useState(false);
  const [resultadoCusto, setResultadoCusto] = useState<CustoCalculado | null>(null);

  const [formData, setFormData] = useState({
    nome: '',
    skuInterno: '',
    descricao: '',
    categoriaId: '',
    ativo: true,
    destaque: false,
    modelo3D: '',
    arquivo3DUrl: '',
    nomeArquivo3D: '',
    fotos: [] as string[],
    visibilidade: VisibilidadeProduto.CATALOGO,
    exclusivoSalaSecreta: false,
    
    // Ficha Técnica
    filamentos: [] as { filamentoId: string; gramas: number; tipo: string }[],
    tempoImpressaoMinutos: '0',
    tempoPreparacaoMinutos: '5',
    tempoManufaturaMinutos: '0',
    maquinaPadraoId: '',
    quantidadeLote: '1',
    embalagens: [] as { embalagemId: string; quantidade: number }[],
    tipoManufatura: [] as TipoManufatura[],
    videoTimelapseUrl: '',
    
    // Taxas e Precificação
    taxaRefugo: '10',
    taxaDesperdicio: '5',
    markup: '200',
    taxaImpostos: '15',
    precoVenda: '0',
    estoqueDisponivel: '0',
    estoqueMinimo: '5',
    altura: '20',
    largura: '20',
    comprimento: '20',
  });

  // Queries
  const { data: categorias } = useQuery({ queryKey: ['categorias'], queryFn: () => api.get('/categorias').then(res => res.data.data) });
  const { data: filamentos } = useQuery<Filamento[]>({ queryKey: ['filamentos'], queryFn: () => api.get('/insumos/filamentos').then(res => res.data.data) });
  const { data: maquinas } = useQuery<Maquina[]>({ queryKey: ['maquinas'], queryFn: () => api.get('/maquinas').then(res => res.data.data) });
  const { data: embalagens } = useQuery({ queryKey: ['embalagens'], queryFn: () => api.get('/insumos/embalagens').then(res => res.data.data) });

  const handleCalcular = async () => {
    if (formData.filamentos.length === 0) return;

    setIsCalculando(true);
    try {
      const payload = {
        filamentos: formData.filamentos.filter(f => f.filamentoId).map(f => ({ ...f, gramas: Number(f.gramas) })),
        tempoImpressaoMinutos: Number(formData.tempoImpressaoMinutos) || 0,
        tempoPreparacaoMinutos: Number(formData.tempoPreparacaoMinutos) || 0,
        tempoManufaturaMinutos: Number(formData.tempoManufaturaMinutos) || 0,
        maquinaId: formData.maquinaPadraoId || undefined,
        embalagens: formData.embalagens.filter(e => e.embalagemId).map(e => ({ ...e, quantidade: Number(e.quantidade) })),
        markup: Number(formData.markup) || 0,
        taxaImpostos: Number(formData.taxaImpostos) || 0,
        taxaRefugo: Number(formData.taxaRefugo) || 0,
        taxaDesperdicio: Number(formData.taxaDesperdicio) || 0,
        precoVenda: Number(formData.precoVenda) || undefined,
        quantidadeLote: Number(formData.quantidadeLote) || 1
      };

      const response = await api.post('/produtos/calculo/preview', payload);
      const { calculo } = response.data.data;
      setResultadoCusto(calculo);
      
      // Se não houver preço definido, ou for o primeiro cálculo, sugere um
      if (!formData.precoVenda || formData.precoVenda === '0') {
         setFormData(prev => ({ ...prev, precoVenda: calculo.precificacao.precoVendaSugerido.toFixed(2).toString() }));
      }
    } catch (error) {
       console.error('Erro ao calcular custo:', error);
    } finally {
       setIsCalculando(false);
    }
  };

  // Debounce para cálculo automático
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.filamentos.length > 0) {
        handleCalcular();
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [
    formData.filamentos,
    formData.tempoImpressaoMinutos,
    formData.tempoPreparacaoMinutos,
    formData.tempoManufaturaMinutos,
    formData.maquinaPadraoId,
    formData.markup,
    formData.taxaImpostos,
    formData.taxaRefugo,
    formData.taxaDesperdicio,
    formData.embalagens
  ]);

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addFilamento = () => {
    setFormData(prev => ({
      ...prev,
      filamentos: [...prev.filamentos, { filamentoId: '', gramas: 0, tipo: 'PECA' }]
    }));
  };

  const removeFilamento = (index: number) => {
    setFormData(prev => ({
      ...prev,
      filamentos: prev.filamentos.filter((_, i) => i !== index)
    }));
  };

  const updateFilamento = (index: number, data: any) => {
    setFormData(prev => ({
      ...prev,
      filamentos: prev.filamentos.map((f, i) => i === index ? { ...f, ...data } : f)
    }));
  };

  const addEmbalagem = () => {
    setFormData(prev => ({
      ...prev,
      embalagens: [...prev.embalagens, { embalagemId: '', quantidade: 1 }]
    }));
  };

  const removeEmbalagem = (index: number) => {
    setFormData(prev => ({
      ...prev,
      embalagens: prev.embalagens.filter((_, i) => i !== index)
    }));
  };

  const updateEmbalagem = (index: number, data: any) => {
    setFormData(prev => ({
      ...prev,
      embalagens: prev.embalagens.map((e, i) => i === index ? { ...e, ...data } : e)
    }));
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);
    try {
      const uploadFormData = new FormData();
      Array.from(files).forEach(file => uploadFormData.append('files', file));

      const response = await api.post('/upload/multiple', uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const urls = response.data.data.map((f: any) => f.url);
      setFormData(prev => ({ ...prev, fotos: [...prev.fotos, ...urls] }));
    } catch (error) {
       console.error('Erro no upload de fotos:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const removeFoto = (index: number) => {
    setFormData(prev => ({
      ...prev,
      fotos: prev.fotos.filter((_, i) => i !== index)
    }));
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      const response = await api.post('/upload', uploadFormData);
      setFormData(prev => ({
        ...prev,
        arquivo3DUrl: response.data.url,
        nomeArquivo3D: response.data.originalName
      }));
    } catch (error) {
       console.error('Erro no upload:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const criarProduto = useMutation({
    mutationFn: (data: any) => api.post('/produtos', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['produtos'] });
      navigate('/admin/produtos');
    },
    onError: (err: any) => {
      alert('Erro ao criar produto: ' + (err.response?.data?.message || err.message));
    }
  });

  const handleSubmit = () => {
    if (!formData.nome || !formData.skuInterno || !formData.categoriaId) {
      alert('Preencha os campos obrigatórios');
      return;
    }

    const payload = {
      ...formData,
      maquinaPadraoId: formData.maquinaPadraoId || undefined,
      filamentos: formData.filamentos
        .filter(f => f.filamentoId && Number(f.gramas) >= 0)
        .map(f => ({ ...f, gramas: Number(f.gramas) })),
      embalagens: formData.embalagens
        .filter(e => e.embalagemId && Number(e.quantidade) > 0)
        .map(e => ({ ...e, quantidade: Number(e.quantidade) })),
      tempoImpressaoMinutos: Number(formData.tempoImpressaoMinutos) || 0,
      tempoPreparacaoMinutos: Number(formData.tempoPreparacaoMinutos) || 0,
      tempoManufaturaMinutos: Number(formData.tempoManufaturaMinutos) || 0,
      taxaRefugo: Number(formData.taxaRefugo) || 0,
      taxaDesperdicio: Number(formData.taxaDesperdicio) || 0,
      markup: Number(formData.markup) || 0,
      taxaImpostos: Number(formData.taxaImpostos) || 0,
      precoVenda: Number(formData.precoVenda) || 0,
      estoqueDisponivel: Number(formData.estoqueDisponivel) || 0,
      estoqueMinimo: Number(formData.estoqueMinimo) || 0,
      quantidadeLote: Number(formData.quantidadeLote) || 1,
      videoTimelapseUrl: formData.videoTimelapseUrl?.trim() ? (
        formData.videoTimelapseUrl.startsWith('http') ? formData.videoTimelapseUrl : `https://${formData.videoTimelapseUrl}`
      ) : ''
    };

    criarProduto.mutate(payload);
  };

  return (
    <div className="w-full py-6 px-6 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/produtos')} className="rounded-full">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-black text-gray-900 tracking-tight">Novo <span className="text-fort3d-laranja">Produto</span></h1>
            <p className="text-sm text-gray-500 font-medium">Cadastre um novo item com múltiplos materiais</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => navigate('/admin/produtos')}>Cancelar</Button>
          <Button 
            className="bg-fort3d-laranja hover:bg-fort3d-laranja/90 text-white font-bold px-8 shadow-lg shadow-fort3d-laranja/20" 
            onClick={handleSubmit} 
            disabled={criarProduto.isPending}
          >
            {criarProduto.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Salvar Produto
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        <div className="lg:col-span-3">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-gray-100 p-1 rounded-xl w-full justify-start mb-6">
              <TabsTrigger value="geral" className="rounded-lg"><Info className="h-4 w-4 mr-2" /> Informações</TabsTrigger>
              <TabsTrigger value="tecnica" className="rounded-lg"><Layers className="h-4 w-4 mr-2" /> Ficha Técnica</TabsTrigger>
              <TabsTrigger value="preco" className="rounded-lg"><DollarSign className="h-4 w-4 mr-2" /> Preço e Estoque</TabsTrigger>
            </TabsList>

            <TabsContent value="geral" className="space-y-6">
              <Card className="border-none shadow-sm overflow-hidden">
                <CardHeader className="bg-gray-50/50"><CardTitle className="text-lg">Dados Básicos</CardTitle></CardHeader>
                <CardContent className="p-6 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Input label="Nome" value={formData.nome} onChange={(e) => handleChange('nome', e.target.value)} required />
                    <Input label="SKU" value={formData.skuInterno} onChange={(e) => handleChange('skuInterno', e.target.value)} required />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-1.5">
                       <label className="text-sm font-bold text-gray-700">Categoria</label>
                       <Select value={formData.categoriaId} onValueChange={(v) => handleChange('categoriaId', v)}>
                         <SelectTrigger className="bg-white"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                         <SelectContent>{categorias?.map((cat: any) => <SelectItem key={cat.id} value={cat.id}>{cat.nome}</SelectItem>)}</SelectContent>
                       </Select>
                    </div>
                  </div>
              <Input
                label="Descrição Curta"
                value={formData.descricao}
                onChange={(e) => handleChange('descricao', e.target.value)}
                placeholder="Uma breve descrição do produto..."
              />

              <div className="space-y-4">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Upload className="h-4 w-4 text-fort3d-laranja" />
                  Link Vídeo Timelapse (YouTube)
                </label>
                <Input
                  value={formData.videoTimelapseUrl}
                  onChange={(e) => handleChange('videoTimelapseUrl', e.target.value)}
                  placeholder="https://www.youtube.com/watch?v=..."
                  className="bg-gray-50 border-gray-200 focus:ring-fort3d-laranja"
                />
                {formData.videoTimelapseUrl && formData.videoTimelapseUrl.includes('youtube.com') && (
                  <div className="aspect-video rounded-xl overflow-hidden bg-black mt-2">
                    <iframe 
                      width="100%" 
                      height="100%" 
                      src={`https://www.youtube.com/embed/${formData.videoTimelapseUrl.split('v=')[1]?.split('&')[0]}`}
                      title="YouTube video player" 
                      frameBorder="0" 
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                      allowFullScreen
                    ></iframe>
                  </div>
                )}
              </div>    <div className="h-px bg-gray-200 w-full" />
                  <div className="space-y-4">
                    <label className="text-sm font-bold text-gray-700">Fotos</label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 gap-4">
                      {formData.fotos.map((foto, index) => (
                        <div key={index} className="relative aspect-square rounded-xl border border-gray-200 overflow-hidden group shadow-sm bg-gray-100">
                          <img src={foto} className="w-full h-full object-cover" alt="" />
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <Button variant="destructive" size="icon" onClick={() => removeFoto(index)} className="h-8 w-8 rounded-full"><Trash2 className="h-4 w-4" /></Button>
                          </div>
                        </div>
                      ))}
                      <label className="aspect-square rounded-xl border-2 border-dashed border-gray-300 flex flex-col items-center justify-center hover:bg-fort3d-laranja/5 cursor-pointer">
                        {isUploading ? <Loader2 className="h-6 w-6 text-fort3d-laranja animate-spin" /> : <Upload className="h-6 w-6 text-gray-400" />}
                        <span className="text-[10px] text-gray-500 font-bold mt-2">Clique aqui</span>
                        <input type="file" className="hidden" multiple accept="image/*" onChange={handleImageUpload} />
                      </label>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="tecnica" className="space-y-6">
              <Card className="border-none shadow-sm">
                <CardContent className="p-6 space-y-8">
                  <div className="p-6 border-2 border-dashed border-gray-200 rounded-2xl bg-gray-50/50 relative overflow-hidden">
                    <div className="flex items-start gap-4">
                       <div className="p-3 bg-white rounded-xl shadow-sm"><FileCode className="h-6 w-6 text-fort3d-laranja" /></div>
                       <div className="flex-1">
                          <h4 className="font-bold text-gray-900">Arquivo 3D (STL/3MF)</h4>
                          {!formData.arquivo3DUrl ? (
                            <div className="mt-2">
                               <input type="file" id="file-3d" className="hidden" accept=".stl,.3mf" onChange={handleFileUpload} />
                               <Button variant="outline" size="sm" onClick={() => document.getElementById('file-3d')?.click()} disabled={isUploading}>
                                 {isUploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />} Upload Projeto
                               </Button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-3 p-2 bg-white rounded-lg border border-green-200 w-fit mt-2">
                               <Badge className="bg-green-100 text-green-700 hover:bg-green-100 px-2 py-0.5 rounded-md border-none">OK</Badge>
                               <span className="text-xs font-medium text-gray-700 truncate max-w-[150px]">{formData.nomeArquivo3D}</span>
                               <Button variant="ghost" size="icon" className="h-6 w-6 rounded-full" onClick={() => handleChange('arquivo3DUrl', '')}><X className="h-3 w-3" /></Button>
                            </div>
                          )}
                       </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 font-bold text-gray-800"><Layers className="h-5 w-5 text-gray-400" /> Materiais</div>
                      <Button onClick={addFilamento} variant="outline" size="sm"><Plus className="h-4 w-4 mr-1" /> Add</Button>
                    </div>
                    <div className="space-y-3">
                      {formData.filamentos.map((item, index) => (
                        <div key={index} className="grid grid-cols-1 md:grid-cols-12 gap-4 p-4 bg-white border border-gray-200 rounded-xl items-end relative hover:border-fort3d-laranja/30">
                          <div className="md:col-span-5 flex flex-col gap-1.5">
                             <label className="text-[10px] uppercase font-black text-gray-400">Filamento</label>
                             <Select value={item.filamentoId} onValueChange={(v) => updateFilamento(index, { filamentoId: v })}>
                                <SelectTrigger className="bg-gray-50/50"><SelectValue placeholder="Escolha..." /></SelectTrigger>
                                <SelectContent>{filamentos?.map(f => <SelectItem key={f.id} value={f.id}>{f.fabricante} {f.cor}</SelectItem>)}</SelectContent>
                             </Select>
                          </div>
                          <div className="md:col-span-3 flex flex-col gap-1.5">
                             <label className="text-[10px] uppercase font-black text-gray-400">Gramas (g)</label>
                             <Input type="number" className="bg-gray-50/50" value={item.gramas} onChange={(e) => updateFilamento(index, { gramas: Number(e.target.value) })} />
                          </div>
                          <div className="md:col-span-3 flex flex-col gap-1.5">
                             <label className="text-[10px] uppercase font-black text-gray-400">Tipo</label>
                             <Select value={item.tipo} onValueChange={(v) => updateFilamento(index, { tipo: v })}>
                                <SelectTrigger className="bg-gray-50/50"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                   <SelectItem value="PECA">Peça</SelectItem>
                                   <SelectItem value="SUPORTE">Suporte</SelectItem>
                                   <SelectItem value="DETALHE">Detalhe</SelectItem>
                                </SelectContent>
                             </Select>
                          </div>
                          <div className="md:col-span-1 pb-1 flex justify-center">
                            <Button variant="ghost" size="icon" onClick={() => removeFilamento(index)}><Trash2 className="h-4 w-4" /></Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-4 border-t border-gray-100">
                     <div className="space-y-4">
                        <div className="flex items-center gap-2 font-bold text-gray-800"><Clock className="h-5 w-5 text-gray-400" /> Tempos de Operação</div>
                        <div className="grid grid-cols-2 gap-4">
                           <div className="space-y-1"><label className="text-[10px] font-bold text-gray-500">Impressão (min)</label><Input type="number" className="bg-gray-50/50" value={formData.tempoImpressaoMinutos} onChange={(e) => handleChange('tempoImpressaoMinutos', e.target.value)} /></div>
                           <div className="space-y-1"><label className="text-[10px] font-bold text-gray-500">Mão de Obra (min)</label><Input type="number" className="bg-gray-50/50" value={formData.tempoPreparacaoMinutos} onChange={(e) => handleChange('tempoPreparacaoMinutos', e.target.value)} /></div>
                        </div>
                     </div>
                     <div className="space-y-4">
                        <div className="flex items-center gap-2 font-bold text-gray-800"><Zap className="h-5 w-5 text-gray-400" /> Máquina e Lote</div>
                        <div className="grid grid-cols-2 gap-4">
                           <Select value={formData.maquinaPadraoId} onValueChange={(v) => handleChange('maquinaPadraoId', v)}>
                             <SelectTrigger className="bg-gray-50/50"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                             <SelectContent>{maquinas?.map(m => <SelectItem key={m.id} value={m.id}>{m.nome}</SelectItem>)}</SelectContent>
                           </Select>
                           <div className="space-y-1">
                              <Input 
                                type="number" 
                                placeholder="Qtd no Lote" 
                                className="bg-gray-50/50 h-10" 
                                value={formData.quantidadeLote} 
                                onChange={(e) => handleChange('quantidadeLote', e.target.value)} 
                              />
                              <p className="text-[9px] text-gray-400 font-medium">Divisor de custo</p>
                           </div>
                        </div>
                     </div>
                  </div>

                  <div className="space-y-4 pt-6 border-t border-gray-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 font-bold text-gray-800"><Package className="h-5 w-5 text-gray-400" /> Insumos Adicionais e Embalagens</div>
                      <Button onClick={addEmbalagem} variant="outline" size="sm" className="h-8 border-dashed border-gray-300 hover:border-fort3d-laranja hover:text-fort3d-laranja">
                        <Plus className="h-4 w-4 mr-1" />
                        Adicionar Insumo
                      </Button>
                    </div>
                    
                    {formData.embalagens.length === 0 ? (
                      <div className="text-center py-8 bg-gray-50/50 rounded-2xl border border-dashed border-gray-200">
                        <p className="text-xs text-gray-400 font-medium">Nenhum insumo adicional selecionado (opcional)</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {formData.embalagens.map((item, index) => (
                          <div key={index} className="grid grid-cols-1 md:grid-cols-12 gap-4 p-4 bg-white border border-gray-200 rounded-xl items-end relative hover:border-fort3d-laranja/30 animate-in fade-in slide-in-from-top-1 duration-200">
                            <div className="md:col-span-8 flex flex-col gap-1.5">
                              <label className="text-[10px] uppercase font-black text-gray-400">Insumo/Embalagem</label>
                              <Select 
                                value={item.embalagemId} 
                                onValueChange={(val) => updateEmbalagem(index, { embalagemId: val })}
                              >
                                <SelectTrigger className="bg-gray-50/50">
                                  <SelectValue placeholder="Selecione o Insumo" />
                                </SelectTrigger>
                                <SelectContent>
                                  {embalagens?.map((emb: any) => (
                                    <SelectItem key={emb.id} value={emb.id}>
                                      {emb.nome} (R$ {Number(emb.custoUnitario).toFixed(2)})
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="md:col-span-3 flex flex-col gap-1.5">
                              <label className="text-[10px] uppercase font-black text-gray-400">Quantidade</label>
                              <Input
                                type="number"
                                value={item.quantidade}
                                onChange={(e) => updateEmbalagem(index, { quantidade: Number(e.target.value) })}
                                className="bg-gray-50/50"
                              />
                            </div>
                            <div className="md:col-span-1 pb-1 flex justify-center">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => removeEmbalagem(index)}
                                className="h-10 w-10 text-gray-400 hover:text-red-500 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="preco" className="space-y-6">
               <Card className="border-none shadow-sm">
                  <CardHeader><CardTitle>Venda</CardTitle></CardHeader>
                  <CardContent className="p-6 space-y-6">
                     <div className="grid grid-cols-1 md:grid-cols-3 gap-6 bg-gray-50 p-6 rounded-2xl">
                        <div className="space-y-1"><label className="text-xs font-bold text-gray-500">Markup (%)</label><Input type="number" className="h-12 text-lg font-bold" value={formData.markup} onChange={(e) => handleChange('markup', e.target.value)} /></div>
                        <div className="space-y-1"><label className="text-xs font-bold text-fort3d-laranja">Preço de Venda (R$)</label><Input type="number" className="h-12 text-xl font-black text-fort3d-laranja bg-fort3d-laranja/5" value={formData.precoVenda} onChange={(e) => handleChange('precoVenda', e.target.value)} /></div>
                        <div className="space-y-1"><label className="text-xs font-bold text-gray-500">Estoque Atual</label><Input type="number" className="h-12 text-lg font-bold" value={formData.estoqueDisponivel} onChange={(e) => handleChange('estoqueDisponivel', e.target.value)} /></div>
                     </div>
                  </CardContent>
               </Card>
            </TabsContent>
          </Tabs>
        </div>

        <div className="lg:col-span-1">
          <div className="sticky top-24 space-y-6">
            <Card className="border-none shadow-xl bg-gray-900 border-l-4 border-fort3d-laranja text-white overflow-hidden">
               <CardHeader className="pb-2"><CardTitle className="text-sm font-bold uppercase tracking-widest text-gray-400">Total do Lote</CardTitle></CardHeader>
               <CardContent className="space-y-6">
                 <div className="text-4xl font-black text-fort3d-laranja">R$ {resultadoCusto?.custoLote.toFixed(2) || '0,00'}</div>
                 <div className="h-px bg-white/10 w-full" />
                 
                 <div className="space-y-4">
                    <div>
                      <div className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mb-1">Custo por Peça</div>
                      <div className="text-2xl font-black">R$ {resultadoCusto?.custoRealTotal.toFixed(2) || '0,00'}</div>
                    </div>

                    <div className="space-y-2 text-[10px] text-gray-400">
                      <div className="flex justify-between"><span>Filamento Unit:</span><span className="font-bold text-white">R$ {( (resultadoCusto?.composicao.filamentoProduto || 0) + (resultadoCusto?.composicao.filamentoSuporte || 0) ).toFixed(2)}</span></div>
                      <div className="flex justify-between"><span>Máquina/Energ Unit:</span><span className="font-bold text-white">R$ {( (resultadoCusto?.composicao.energia || 0) + (resultadoCusto?.composicao.depreciacao || 0) ).toFixed(2)}</span></div>
                      <div className="flex justify-between"><span>Mão Obra Unit:</span><span className="font-bold text-white">R$ {( (resultadoCusto?.composicao.manufatura || 0) + (resultadoCusto?.composicao.preparacao || 0) ).toFixed(2)}</span></div>
                    </div>
                 </div>
                 
                 <div className="h-px bg-white/10 w-full" />
                 <div className="flex justify-between items-center py-2 px-1">
                    <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Sugerido p/ Peça</span>
                    <span className="text-2xl font-black text-fort3d-laranja">R$ {resultadoCusto?.precificacao.precoVendaSugerido.toFixed(2) || '0,00'}</span>
                 </div>

                 <div className="p-4 bg-white/5 rounded-xl border border-white/5">
                    <div className="text-xs font-bold text-green-400 mb-1 flex items-center gap-2"><TrendingUp className="h-3.5 w-3.5" /> LUCRO P/ PEÇA (ATUAL)</div>
                    <div className="text-2xl font-black text-green-400">R$ {resultadoCusto?.precificacao.lucroPorPeca.toFixed(2) || '0,00'}</div>
                    <p className="text-[10px] text-gray-400 mt-1 font-medium">{resultadoCusto?.precificacao.margemLucroPercent.toFixed(1)}% de margem real</p>
                 </div>
               </CardContent>
               <CardFooter className="bg-white/10 py-3 block text-center">
                   {isCalculando ? <div className="text-[10px] font-bold text-gray-400 animate-pulse">ATUALIZANDO...</div> : <div className="text-[10px] text-gray-500 font-bold uppercase tracking-tighter">Cálculo em tempo real ativado</div>}
               </CardFooter>
            </Card>
            <div className="p-4 rounded-xl border border-yellow-200 bg-yellow-50 flex gap-3">
               <AlertTriangle className="h-5 w-5 text-yellow-600 shrink-0" />
               <div className="text-[10px] text-yellow-700 font-medium">Revisão obrigatória antes da postagem no catálogo.</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
